import React from 'react';
import { NumberFormat } from './constants/Regex';
import { Paragraph } from '@sentara-health/optimahealth_web';

// Medical abbreviations that should always be in ALL CAPS
const MEDICAL_ABBREVIATIONS = [
  'HMO', 'PPO', 'EPO', 'POS', 'HDHP', 'HSA', 'FSA', 'HRA', 'OOP', 'OOPM', 'COB',
  'DED', 'COINS', 'EOB', 'FFS', 'UCR',
  'ICD-10', 'CPT', 'HCPCS', 'NPI', 'TIN', 'PA', 'ERA', 'EFT',
  'PC', 'PLLC', 'LLC', 'PA', 'ASC', 'UC', 'DME', 'SNF', 'FQHC', 'RHC',
  'DR', 'MD', 'DO', 'NP', 'PMHNP', 'PA', 'RN', 'LPN', 'LVN', 'CNA',
  'PT', 'OT', 'ST', 'SLP', 'RT',
  'RX', 'OTC', 'PBM', 'DAW', 'NDC',
  'BP', 'HR', 'RR', 'BMI', 'CBC', 'MRI', 'CT',
  'ER', 'ED', 'ICU', 'OR', 'PACU', 'EMS', 'OP',
];

// Address abbreviations that should always be in ALL CAPS
const ADDRESS_ABBREVIATIONS_IN_CAPS = [
  'PO', 'APT', 'STE', 'DEPT',
  'RD', 'NW', 'NE', 'SW', 'SE',
  'LTD', 'PVT', 'LLC', 'INC', 'CORP', 'CO',
  'US', 'USA'
] as const;

const medicalAbbreviationsSet = new Set(
  MEDICAL_ABBREVIATIONS.map(abbr => abbr.toUpperCase())
);

const addressAbbreviationsSet = new Set(
  ADDRESS_ABBREVIATIONS_IN_CAPS.map(abbr => abbr.toUpperCase())
);

export const formatMedicalAbbreviations = (text: string): string => {
  if (!text) return text;

  const tokens = text.split(/([/\-,\s]+)/);

  return tokens.map(token => {
    const trimmed = token.trim();
    if (!trimmed || /^[/\-,\s]+$/.test(token)) {
      return token;
    }

    const upper = trimmed.toUpperCase();
    if (medicalAbbreviationsSet.has(upper)) {
      return token.replace(trimmed, upper);
    }

    return token;
  }).join('');
};

export const toSentancePascalCase = (text: string) => {
  return text
    ?.trim()
    ?.replaceAll("  ", " ")
    ?.split(' ')
    ?.map((word) => toPascalCase(word))
    ?.join(' ');
};

/**
 * Formats text to pascal case with medical abbreviations properly capitalized.
 * Use this for service types, provider names, and other medical-related text.
 */
export const formatMedicalText = (text: string): string => {
  const pascalCased = toSentancePascalCase(text);
  return formatMedicalAbbreviations(pascalCased);
};

export const toPascalCase = (str: string) => {
  if (!str) {
    return str;
  }
  return str?.length > 0
    ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
    : str;
};

export const dataIdDynamic = (item: string) => {
  return `${item?.charAt(0).toLowerCase() + item?.slice(1)}`.replace(/\s/g, '');
};

export function equalsIgnoringCase(text: string, other: string) {
  return text.localeCompare(other, undefined, { sensitivity: 'base' }) === 0;
}

//formating phone number'1234567890' to '(123) 456-7890'
export function formatePhoneNo(text: string): string {
  let input = "";
  if (text?.length) {
    input = text.replace(NumberFormat, '');
    if (input.length > 3 && input.length <= 6)
      input = `(${input.slice(0, 3)}) ${input.slice(3)}`;
    else if (input.length > 6)
      input = `(${input.slice(0, 3)}) ${input.slice(3, 6)}-${input.slice(6, 10)}`;
  }
  return input;
}

/**
 * Formats address text in Title Case with preservation of known abbreviations.
 * 
 * Rules:
 * - Converts each token to Title Case using toPascalCase
 * - Preserves separators (spaces, commas, hyphens, slashes)
 * - Items in ADDRESS_ABBREVIATIONS_IN_CAPS remain ALL CAPS (PO, APT, STE, LLC, INC, etc.)
 * 
 * @param text - The address text to format
 * @returns The formatted address text
 */
export const addressFormatter = (text: string): string => {
  if (!text || typeof text !== 'string') {
    return '';
  }

  const trimmedText = text.trim();

  // Split on separators but preserve them using capturing group
  const tokens = trimmedText.split(/([/\-,\s]+)/);

  return tokens.map((token) => {
    // If it's a separator, keep it as-is
    if (/^[/\-,\s]+$/.test(token)) {
      return token;
    }

    const upperToken = token.trim().toUpperCase();

    // Check if it's a known abbreviation
    if (upperToken && addressAbbreviationsSet.has(upperToken)) {
      return upperToken;
    }

    // Otherwise, apply Title Case
    return toPascalCase(token);
  }).join('');
};


// New utility function to format Paragraph component with i18Next Trans content and classes to create list formatting
export const listType = (text: string, className: string, level?: number): React.ReactNode[] => {
  if (typeof text !== "string") {
    throw new Error("Need a string");
  }

  // Split into lines, preserving empty lines
  const lines = text.split(/\r?\n\r?\n/);

  // Map each line with its number
  return lines
    .map((line, index) => {
      const lineText = `${line}`;
      return React.createElement(React.Fragment, { key: index }, React.createElement(Paragraph, { text: lineText, className: className, level: level }));
    });
}